import { ColDef, PostSortRowsParams, RowClickedEvent } from "ag-grid-community";
import { UnorderedListOutlined } from "@ant-design/icons";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useMemo, useRef, useState } from "react";
import { aggridHeaderClass, MaximizeIcon, RestoreIcon } from "src/components/common/common";
import useUiuxStore from "src/store/useUiuxStore";
import { CreateCellClickHandler, createCellCssClassRules, CreateCellDoubleClickHandler, CustomCellClickAction, CustomCellDoubleClickAction, GridCellConfig, InsertRowHandler, postSortHandler, RemoveRowHandler, useCellChangeHandler, useRowSelectionHandler } from "src/utils/grid/GridHandlerContent";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { defaultColDef, getDefaultRowIndexColumnConfig, getRowStyleFlags, IndexParameterCell } from "src/utils/grid/gridStyleUtils";
import { useKSK2020_R01Store } from "../Store_KSK2020_R01";

import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { defaultKSK2020_R01LotDto, KSK2020_R01LotDto, KSK2020_R01InspectDto, KSK2020_R01DetailDto, defaultKSK2020_R01HeaderDto } from "../Dto_KSK2020_R01";

import Grid_Excel from "src/components/common/gridWrapper/Grid_Excel";
import { useDetailTableHandlers as useCommonDetailTableHandlers } from "src/hooks/useDetailTableHandlers";

// 범용 훅 임포트
import { useDetailTableEvents } from "src/hooks/detail-table/useDetailTableEvents";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { calculateDetailAmount } from "src/utils/calculation/calculationUtils";
import { getAllWorkPlaces } from "src/apis/workPlaceApi";
import { InfoCodeSearchFieldConfig } from "src/types/info-form";
import { getAllWarehouses } from "src/apis/warehouseApi";
import { useInfoFormEvents } from "src/hooks/useInfoFormEvents";
import { DetailTable_KSK2020_R01 } from "./DetailTable_KSK2020_R01";
import { useDescText } from "src/hooks/useDescText";
import { DESC_BTN, DESC_FLD, formatDescCount } from "src/utils/descLanguage/descLanguageUtil";

const COLUMN_STATE_KEY = 'KSK2020_R01-lot-grid-column-state';

const GRID_CELL_CONFIG: GridCellConfig = {
	disabledCells: ['lotNo', 'lotQty'],
	insertedDisabledCells: [''],
	commonDisabledCells: [''],
	
	pointerCells: [''],
	insertedPointerCells: [''],
	commonPointerCells: [''],
	
	alignLeftCells: [''],
};

interface Props {
	lotGridRef: React.RefObject<AgGridReact<KSK2020_R01LotDto> | null>;
	defaultKSK2020_R01LotRow: KSK2020_R01LotDto;
	isMaximized?: boolean;
	onToggleMaximize?: () => void;
}

export const LotTable_KSK2020_R01 = (props: Props) => {

	const { lotGridRef, defaultKSK2020_R01LotRow, isMaximized = false, onToggleMaximize } = props;

	const { 
			KSK2020_R01Filter, setKSK2020_R01Filter,
			KSK2020_R01LotList, setKSK2020_R01LotList, 
			editKSK2020_R01, setEditKSK2020_R01,
			selectedKSK2020_R01LotList, setSelectedKSK2020_R01LotList, removeSelectedKSK2020_R01LotList, addSelectedKSK2020_R01LotList, initSelectedKSK2020_R01LotList,
			deletedKSK2020_R01LotList, setDeletedKSK2020_R01LotList, addDeletedKSK2020_R01LotList, removeDeletedKSK2020_R01LotList,
			updateKSK2020_R01LotList,
			lotColumnState, setLotColumnState,
			originalKSK2020_R01LotList, setOriginalKSK2020_R01LotList,
		} = useKSK2020_R01Store();
	const { setCodeSearchModalProps } = useUiuxStore();
	const { itemStatusList } = useCommonCodeStore();
	const { renderDescText } = useDescText();
	const [clickable, setClickable] = useState(true);

	//const gridRef = useRef<AgGridReact>(null);
	const gridWrapperRef = useRef<HTMLDivElement>(null); // 1. div를 위한 ref 생성

	const {
		getRowId,
		createRowClassRules,
		createCellClassRules,
		paddedGridData,
		renderIndexCell,
	} = useGridSettingContent({
		gridRef: lotGridRef,
		key: 'valueIndex',
		gridData: KSK2020_R01LotList,
		defaultGridData: defaultKSK2020_R01LotRow,
		selectedRow: selectedKSK2020_R01LotList,
		originalGridData: originalKSK2020_R01LotList,
		updateRow: (index: number, data: any) => updateKSK2020_R01LotList(index, data),
	});
	
	

	const headerNames = useMemo<Record<string, string>>(() => ({
		rowIndex: "",
		lotQty: renderDescText(DESC_FLD.PRODUCTION_QTY, "생산량"),
		lotCd: renderDescText(DESC_FLD.LOT_NO, "LOT No"),
	}), [renderDescText]);

	const {
		disabledInsertedColumns, disabledColumns,
		pointerInsertedColumns, pointerColumns,
		alignLeftColumns,
	} = createCellCssClassRules(GRID_CELL_CONFIG);

	const gridDefaultColDef = useMemo(() => {
        const baseRules = createCellClassRules<KSK2020_R01LotDto>({
            idKey: 'valueIndex',
            selectedList: selectedKSK2020_R01LotList,
            deletedList: deletedKSK2020_R01LotList,
            disabledInsertedColumns,	disabledColumns,
            pointerInsertedColumns,		pointerColumns,
            alignLeftColumns,
        });

        return { ...defaultColDef, cellClassRules: { ...baseRules, }}; 
	}, [
        selectedKSK2020_R01LotList, deletedKSK2020_R01LotList,
        disabledInsertedColumns, disabledColumns,         
        pointerInsertedColumns, pointerColumns,
		alignLeftColumns,
    ]);

	const baseColumnDefs: ColDef<KSK2020_R01LotDto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(
			// (params: KSK2020_R01InspectDto) => renderIndexCell(
			// 	params, 
			// 	(innerParams: KSK2020_R01LotDto) => getRowStyleFlags (
			// 		innerParams,
			// 		{
			// 			deletedList: deletedKSK2020_R01LotList,
			// 			selectedList: selectedKSK2020_R01LotList,
			// 			dataList: KSK2020_R01LotList
			// 		}),
			// 	(rowIndex: number, e: React.MouseEvent) => {},
			// 	(rowData) => RemoveRowHandler( 
			// 		lotGridRef,
			// 		KSK2020_R01LotList, 
			// 		rowData,  // rowData는 renderIndexCell에서 Dto 형식으로 선언함
			// 		setClickable, 
			// 		removeKSK2020_R01LotList, 
			// 		(valueIndex) => addDeletedKSK2020_R01LotList([valueIndex.toString()])
			// 	),
			// )
		),
		{
			headerName: renderDescText(DESC_FLD.PRODUCTION_QTY, "생산량"), colId: 'lotQty', field: 'lotQty', width: 80,
			editable: (params: any) => params.data.inserted === true,
			pasteable: true,
			valueFormatter: (params: any) =>
				params.value != null ? params.value.toLocaleString() : '',
		},
        {
			headerName: renderDescText(DESC_FLD.LOT_NO, "LOT No"), colId: 'lotNo', field: 'lotNo', width: 150,
			editable: false,
		},
		// ...IndexParameterCell(),
	], [deletedKSK2020_R01LotList, KSK2020_R01LotList, renderDescText]);

	const columnDefs: ColDef<KSK2020_R01LotDto>[] = useMemo(() => {
		if (lotColumnState === null) {
			return baseColumnDefs;
		}
		
		return GridCustomColumnDefs<KSK2020_R01LotDto>({
			baseColumnDefs,
			columnState: lotColumnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass
		});
	}, [baseColumnDefs, lotColumnState, headerNames]);

	
	const handleCellChange = useCellChangeHandler({
		dataList: KSK2020_R01LotList,
		originalDataList: originalKSK2020_R01LotList,
		updateRow: updateKSK2020_R01LotList,
		customBeforeUpdate: (updatedData, field, value) => {
			return updatedData;
		},
		customAfterUpdate: (updatedData, field, value) => {
			return updatedData;
		},
	});
	

	const handleRowClick = useRowSelectionHandler({
		dataList: KSK2020_R01LotList,
		idKey: 'valueIndex',
		selectedList: selectedKSK2020_R01LotList,
		deletedList: deletedKSK2020_R01LotList,
		addSelected: addSelectedKSK2020_R01LotList,
		removeSelected: removeSelectedKSK2020_R01LotList,
		clickable, setClickable
	});

	const commonInputColumns = ['lotNo', 'lotQty', 'remark', ];

	// 범용 이벤트 훅을 위한 설정
	const defaultEditableColumns = ['lotNo', 'lotQty', 'remark'];


	// 범용 이벤트 훅 사용 (코드 검색 없이) - 방향키 이동 기능 유지
	/**
	 * 기본 상세 테이블 이벤트 핸들러 훅
	 * 
	 * 주요 기능:
	 * - 키다운 네비게이션 (방향키 이동)
	 * - 셀 클릭/키다운 이벤트 처리
	 * 
	 * @param detailList - 상세 데이터 목록
	 * @param editableColumns - 편집 가능한 컬럼 목록
	 */
	const cellEventHandlers = useDetailTableEvents({
		detailList: KSK2020_R01LotList as any,
		editableColumns: defaultEditableColumns,
		keyDownConfig: {
			enableArrowNavigation: true,
		}
	});
	
	const cellClickActions: CustomCellClickAction<KSK2020_R01LotDto>[] = useMemo(() => [
		{
			validate: (field, data) => {
				return commonInputColumns.includes(field);
			},
			//aggrid 기본 편집 기능
			action: (e) => { e.api.startEditingCell({ rowIndex: e.rowIndex!, colKey: e.colDef.field! }); }
		},
	], []);
	const handleCellClick = CreateCellClickHandler(KSK2020_R01LotList, 'valueIndex', cellClickActions);

	//더블클릭 액션 없음
	const cellDoubleClickActions: CustomCellDoubleClickAction<KSK2020_R01LotDto>[] = useMemo(() => [
	], [editKSK2020_R01?.itemCd]);
	const handleCellDoubleClick = CreateCellDoubleClickHandler(KSK2020_R01LotList, 'valueIndex', cellDoubleClickActions);

	const handlePostSort = useCallback((params: PostSortRowsParams<KSK2020_R01LotDto>) => {
		postSortHandler(params, KSK2020_R01LotList, originalKSK2020_R01LotList, setKSK2020_R01LotList);
	}, [KSK2020_R01LotList, originalKSK2020_R01LotList, ]);

	const pinnedBottomRowData = useMemo(() => {
		// 삭제된 행은 제외하고 수량 합계 계산
		const totalQty : number = KSK2020_R01LotList
			.reduce((sum, item) => sum + (deletedKSK2020_R01LotList.includes(item.valueIndex.toString()) ? 0 : Number(item.lotQty) || 0), 0);

		return [{
			lotNo: formatDescCount(
				KSK2020_R01LotList.length,
				renderDescText(DESC_FLD.COUNT_DISPLAY, "{count}건"),
			),
			lotQty: totalQty,
		}];	  
	}, [KSK2020_R01LotList, deletedKSK2020_R01LotList, renderDescText]);

	return (
		<>
			<div className="flex h-full min-h-full flex-1 overflow-hidden rounded-[12px] border border-[#d3e0f0] bg-white shadow-sm">
				<GridContextWrapper<KSK2020_R01LotDto>
					gridWrapperRef={gridWrapperRef}
					gridRef={lotGridRef}
					paddedGridData={paddedGridData}
					setPaddedGridData={setKSK2020_R01LotList}
					initSelected={initSelectedKSK2020_R01LotList}
					defaultRowData={defaultKSK2020_R01LotRow}
					baseColumnDefs={columnDefs}	
					setColumnState={setLotColumnState}
					localStorageKey={COLUMN_STATE_KEY}
					menuItems={undefined}
				>
					<div className="flex h-[36px] shrink-0 items-center gap-[10px] bg-gradient-to-r from-[#123f78] to-[#245d98] px-[16px] text-[17px] font-bold text-white">
						<UnorderedListOutlined />
						<span>{renderDescText(DESC_FLD.GENERATED_LOT_LIST, "발생 Lot 목록")}</span>
						<button type="button" className="ml-auto flex h-[30px] w-[34px] items-center justify-center rounded-[6px] border border-white/40 bg-white/10 hover:bg-white/20" onClick={onToggleMaximize} title={isMaximized ? renderDescText(DESC_BTN.MINIMIZE, "최소화") : renderDescText(DESC_BTN.MAXIMIZE, "최대화")}>
							{isMaximized ? <RestoreIcon /> : <MaximizeIcon />}
						</button>
					</div>
					<Grid_Excel<KSK2020_R01LotDto>
						ref={lotGridRef}
						rowData={paddedGridData}	getRowId={getRowId} // +- 버튼 테이블 getRowId는 valueIndex로 고정
						columnDefs={columnDefs}		
						defaultColDef={gridDefaultColDef}
						overlayNoRowsTemplate={`<span style="padding: 32px;">${renderDescText(DESC_FLD.NO_DATA, "조회된 데이터가 없습니다.")}</span>`}

						rowClassRules={createRowClassRules<KSK2020_R01LotDto>({
							idKey: 'valueIndex',
							selectedList: selectedKSK2020_R01LotList,
							deletedList: deletedKSK2020_R01LotList
						})}

						onRowClicked={handleRowClick}
						onCellClicked={handleCellClick}
						onCellDoubleClicked={handleCellDoubleClick}
						onCellKeyDown={cellEventHandlers.handleCellKeyDown}
						onCellValueChanged={handleCellChange}
						pinnedBottomRowData={pinnedBottomRowData}
						

						onPostSortRows={handlePostSort} //onSortChanged 삭제, 커스텀 정렬 기능추가 
						// Grid_Select에 공통 로직 처리를 위한 props 전달
						columnStateKey={COLUMN_STATE_KEY}			//컬럼 상태 키(로컬스토리지)	
						setColumnState={setLotColumnState}		//컬럼 상태 저장
					/>
				</GridContextWrapper>
			</div>
		</>
	)
}
